import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import type { AssistantSummaryData } from '@cumpli/shared';
import { Pill, friendlyEstado } from '../../ui';
import { useCountUp } from '../../../lib/motion';
import { CardHeader, CardShell } from './shell';
import { MaybeRedacted, isRedacted } from './redacted';
import { asEstado } from './labels';

/** A headline tile. When `count` is a number and `animate` is on, it counts up 0→count. */
function Stat({
  label,
  value,
  count,
  animate,
}: {
  label: string;
  value: ReactNode;
  count?: number | undefined;
  animate: boolean;
}) {
  const shouldCount = animate && count !== undefined;
  const n = useCountUp(count ?? 0, { enabled: shouldCount });
  return (
    <div className="rounded-lg bg-canvas px-3 py-2.5 text-center">
      <div className="font-serif text-xl font-semibold leading-none text-ink">
        {shouldCount ? Math.round(n) : value}
      </div>
      <div className="mt-1 font-mono text-[9px] uppercase leading-tight tracking-wide text-ink-faint">
        {label}
      </div>
    </div>
  );
}

/**
 * `get_dashboard_summary` — tenant panorama: three headline tallies (total
 * expedientes, open alerts, high-risk count derived from `byRiesgo`), the
 * estado breakdown as pills, and a jump into the full panel. On a fresh answer
 * (`animate`) the tallies count up so the panorama lands rather than snaps.
 */
export function SummaryCard({
  data,
  animate = false,
}: {
  data: AssistantSummaryData;
  animate?: boolean | undefined;
}) {
  const alto = data.byRiesgo.find((b) => b.riesgo === 'alto');
  // When the risk labels themselves are redacted we can't tally "alto" —
  // show the redaction dash instead of a misleading 0 (fails safe either way).
  const riesgoOculto = !alto && data.byRiesgo.some((b) => isRedacted(b.riesgo));
  const alertsCount = isRedacted(data.openAlerts) ? undefined : data.openAlerts;
  const altoCount = alto && !isRedacted(alto.count) ? alto.count : undefined;
  return (
    <CardShell>
      <CardHeader title="Panorama de la cartera" label="Panel" />

      <div className="mt-2.5 grid grid-cols-3 gap-1.5">
        <Stat label="Expedientes" value={data.totalExpedientes} count={data.totalExpedientes} animate={animate} />
        <Stat
          label="Alertas abiertas"
          value={<MaybeRedacted value={data.openAlerts} />}
          count={alertsCount}
          animate={animate}
        />
        <Stat
          label="Alto riesgo"
          value={alto ? <MaybeRedacted value={alto.count} /> : riesgoOculto ? '—' : 0}
          count={altoCount}
          animate={animate}
        />
      </div>

      {data.byEstado.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {data.byEstado.map((b, i) => {
            const key = isRedacted(b.estado) ? null : asEstado(b.estado);
            const friendly = key ? friendlyEstado(key) : null;
            return (
              <Pill key={i} tone={friendly?.tone ?? 'slate'}>
                {friendly ? friendly.label : <MaybeRedacted value={b.estado} />}
                {' · '}
                <MaybeRedacted value={b.count} />
              </Pill>
            );
          })}
        </div>
      )}

      <Link
        to="/panel"
        className="mt-3 inline-flex items-center gap-1 rounded text-[12px] font-semibold text-primary-dark hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        Ver panel <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
      </Link>
    </CardShell>
  );
}
